// src/screens/TruckDetailScreen.tsx
import React, { useState } from 'react';
import { StyleSheet, View, Text, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Appbar, Chip, Surface, useTheme } from 'react-native-paper';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import ServiceScheduleList from '../components/ServiceScheduleList';
import { RootStackParamList } from '../../App';

type TruckDetailRouteProp = RouteProp<RootStackParamList, 'TruckDetail'>;
type TruckDetailNavigationProp = StackNavigationProp<RootStackParamList, 'TruckDetail'>;
type TabKey = 'overview' | 'services';

const TruckDetailScreen: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation<TruckDetailNavigationProp>();
  const route = useRoute<TruckDetailRouteProp>();
  const { truck } = route.params;
  const [activeTab, setActiveTab] = useState<TabKey>('services');

  const renderOverview = () => (
    <ScrollView
      contentContainerStyle={styles.overviewContent}
      showsVerticalScrollIndicator={false}
    >
      <Surface style={styles.overviewCard} elevation={1}>
        <View style={styles.overviewRow}>
          <MaterialCommunityIcons name="card-account-details" size={20} color={theme.colors.primary} />
          <Text style={[styles.overviewLabel, { color: theme.colors.onSurfaceVariant }]}>Plate</Text>
          <Text style={[styles.overviewValue, { color: theme.colors.onSurface }]}>
            {truck.plateNumber}
          </Text>
        </View>
        {truck.model ? (
          <View style={styles.overviewRow}>
            <MaterialCommunityIcons name="truck" size={20} color={theme.colors.primary} />
            <Text style={[styles.overviewLabel, { color: theme.colors.onSurfaceVariant }]}>Model</Text>
            <Text style={[styles.overviewValue, { color: theme.colors.onSurface }]}>{truck.model}</Text>
          </View>
        ) : null}
        {truck.color ? (
          <View style={styles.overviewRow}>
            <MaterialCommunityIcons name="palette" size={20} color={theme.colors.primary} />
            <Text style={[styles.overviewLabel, { color: theme.colors.onSurfaceVariant }]}>Color</Text>
            <Text style={[styles.overviewValue, { color: theme.colors.onSurface }]}>{truck.color}</Text>
          </View>
        ) : null}
        <View style={styles.overviewRow}>
          <MaterialCommunityIcons
            name="circle"
            size={20}
            color={truck.isActive ? '#4CAF50' : '#9E9E9E'}
          />
          <Text style={[styles.overviewLabel, { color: theme.colors.onSurfaceVariant }]}>Status</Text>
          <Chip
            compact
            mode="flat"
            style={[truck.isActive ? styles.activeChip : styles.inactiveChip, { backgroundColor: truck.isActive ? theme.colors.secondaryContainer : theme.colors.elevation.level3 }]}
            textStyle={[truck.isActive ? styles.activeChipText : styles.inactiveChipText, { color: truck.isActive ? theme.colors.onSecondaryContainer : theme.colors.onSurfaceVariant }]}
          >
            {truck.isActive ? 'Active' : 'Inactive'}
          </Chip>
        </View>
      </Surface>
    </ScrollView>
  );

  return (
    <SafeAreaView
      edges={['bottom', 'left', 'right']}
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <Appbar.Header style={[styles.appbar, { backgroundColor: theme.colors.primary }]}>
        <Appbar.BackAction onPress={() => navigation.goBack()} color="white" />
        <Appbar.Content title={truck.plateNumber} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <View
        style={[
          styles.tabBar,
          { backgroundColor: theme.colors.surfaceVariant },
        ]}
      >
        {(['overview', 'services'] as TabKey[]).map(tab => (
          <View
            key={tab}
            style={[
              styles.tab,
              activeTab === tab && { backgroundColor: theme.colors.surface },
            ]}
          >
            <Text
              style={[
                styles.tabLabel,
                {
                  color:
                    activeTab === tab
                      ? theme.colors.primary
                      : theme.colors.onSurfaceVariant,
                },
              ]}
              onPress={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.content}>
        {activeTab === 'overview' ? renderOverview() : <ServiceScheduleList truck={truck} />}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  appbar: { elevation: 0 },
  appbarTitle: { fontFamily: 'Manrope-SemiBold', fontSize: 20, color: 'white' },
  tabBar: { flexDirection: 'row' },
  tab: { flex: 1, alignItems: 'center', paddingVertical: 12, borderRadius: 6 },
  tabLabel: { fontFamily: 'Inter-Medium', fontSize: 14 },
  content: { flex: 1 },
  overviewContent: { padding: 16 },
  overviewCard: { borderRadius: 6, padding: 16 },
  overviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  overviewLabel: { fontFamily: 'Inter-Regular', fontSize: 14, flex: 1 },
  overviewValue: { fontFamily: 'Inter-Medium', fontSize: 14 },
  activeChip: { borderRadius: 9999 },
  inactiveChip: { borderRadius: 9999 },
  activeChipText: { fontFamily: 'Inter-Medium', fontSize: 11 },
  inactiveChipText: { fontFamily: 'Inter-Medium', fontSize: 11 },
});

export default TruckDetailScreen;
